/**
 * Amplify Auth Service Implementation
 * Wraps AWS Amplify authentication methods
 */

import { Amplify } from "aws-amplify";
import {
  fetchUserAttributes as amplifyFetchUserAttributes,
  getCurrentUser as amplifyGetCurrentUser,
  signOut as amplifySignOut,
  fetchAuthSession,
  signInWithRedirect,
} from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { amplifyConfig } from "../../config";
import {
  AuthAttributes,
  AuthEventCallback,
  AuthUser,
  IAuthService,
} from "./IAuthService";

export class AmplifyAuthService implements IAuthService {
  private initialized = false;

  constructor() {
    this.ensureInitialized();
  }

  private ensureInitialized() {
    if (!this.initialized) {
      Amplify.configure(amplifyConfig);
      this.initialized = true;
    }
  }

  async getCurrentUser(): Promise<AuthUser | null> {
    try {
      const user = await amplifyGetCurrentUser();
      return {
        username: user.username,
        userId: user.userId,
      };
    } catch (error) {
      // User not authenticated
      return null;
    }
  }

  async fetchUserAttributes(): Promise<AuthAttributes> {
    const attributes = await amplifyFetchUserAttributes();
    return attributes as AuthAttributes;
  }

  async refreshUserAttributes(): Promise<AuthAttributes> {
    // Force refresh the tokens to get the latest user attributes from Cognito
    await fetchAuthSession({ forceRefresh: true });
    // Now fetch the updated attributes from the new tokens
    const attributes = await amplifyFetchUserAttributes();
    return attributes as AuthAttributes;
  }

  async signInWithRedirect(provider: string): Promise<void> {
    await signInWithRedirect({ provider: provider as any });
  }

  async signOut(): Promise<void> {
    await amplifySignOut();
  }

  listenToAuthEvents(callback: AuthEventCallback): () => void {
    const hubListener = Hub.listen("auth", ({ payload }: any) => {
      callback({
        event: payload.event,
        data: payload.data,
      });
    });

    return hubListener;
  }
}
